import { useFormik } from "formik";
import { useState } from "react";
import { Link,  useParams } from "react-router-dom";
import { PatternFormat } from "react-number-format";
import { CircularProgress, InputLabel, Stack, TextField, Typography } from "@mui/material";
import { Button, Checkbox, FormControlLabel, Grid, Box } from "@mui/material";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import InputAdornment from "@mui/material/InputAdornment";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import InfoIcon from "@mui/icons-material/Info";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CustomizedDialogs from "../../../../../../../UI/footer-common-links/footerModal/footer-modal";
import useToggleState from "../../../../../../../hooks/useTogglePassword";
import { validationSchema, initialValues } from "./validation";
import PasswordCritera from "./passwordCritera";
import ApiClient from "helpers/ApiClient";
import "./styles.scss";

const CorporateAccount = ({ setToggleSnackbar, setUserId, onSignupSuccess }: any) => {
  const { role } = useParams();
  const [signUpEmail, setSignUpEmail] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState(0);

  const [state, toggleState]: any = useToggleState({
    password: false,
    confirmPassword: false,
    passCriteria: false,
  });

  const handleCloseModal = () => {
    setOpenModal(false);
  };
  const handleOpenModal = (modalId: number) => {
    setId(modalId);
    setOpenModal(true);
  };

  const signUpUser = (values: any) => {
    const { email, password, firstName, lastName, phoneNumber } = values;
    setIsLoading(true);
    ApiClient.post("users/signup", {
      email,
      password,
      roles: role,
      phoneNumber,
      firstName: firstName.trim(),
      lastName:lastName.trim(),
    })
      .then((res) => {
        setUserId(res.data.user.id);
        setIsLoading(false);
        setSignUpEmail(email);
        toggleState("hideCriteria");
        setToggleSnackbar({
          message: "Check your email for verification",
          isToggle: true,
          mode: "success",
          top: "110px",
        });
        onSignupSuccess?.(email);
      })
      .catch((error: any) => {
        const err = error.response.data.message;
        setIsLoading(false);
        const defaultError = err.slice(32, 45);
        if (defaultError === "already exist") {
          setToggleSnackbar({
            message: "Email or Phone number is already registered.",
            isToggle: true,
            mode: "error",
            top: "110px",
          });
        } else {
          setToggleSnackbar({ message: err, isToggle: true, mode: "error", top: "110px" });
        }
      });
  };

  const { values, errors, touched, handleChange, handleSubmit, handleBlur } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      signUpUser(values);
    },
  });

  const hidePasswordCriteria = () => {
    toggleState("hideCriteria");
  };

  return (
    <Box className="personal-account">
      {state.passCriteria && (
        <PasswordCritera value={values.password} name="password-criteria-wrapper" />
      )}
      {
        <form onSubmit={handleSubmit}>
          <Typography variant="h5" sx={{ mb: "15px", color: "#434242", fontWeight: "500" }}>
            {role} Account
          </Typography>

          <Grid container spacing={2} rowSpacing={3}>
            <Grid item md={6} xs={12}>
              <InputLabel
                htmlFor="firstName"
                className={errors.firstName && touched.firstName ? "label-err" : "label"}
              >
                First Name <span style={{ color: 'red' }}>*</span>
              </InputLabel>

              <TextField
                name="firstName"
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                error={!!(errors.firstName && touched.firstName)}
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={hidePasswordCriteria}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {errors.firstName && touched.firstName ? (
                        <InfoIcon sx={{ color: "#DC3545" }} fontSize="medium" />
                      ) : (
                        <PersonIcon fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {errors.firstName && touched.firstName && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.firstName}
                </Typography>
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel
                htmlFor="lastName"
                className={errors.lastName && touched.lastName ? "label-err" : "label"}
              >
                Last Name <span style={{ color: 'red' }}>*</span>
              </InputLabel>
              <TextField
                name="lastName"
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                error={!!(errors.lastName && touched.lastName)}
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={hidePasswordCriteria}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {errors.lastName && touched.lastName ? (
                        <InfoIcon sx={{ color: "#DC3545" }} fontSize="medium" />
                      ) : (
                        <PersonIcon fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {errors.lastName && touched.lastName && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.lastName}
                </Typography>
              )}
            </Grid>
            <Grid item md={12} xs={12}>
              <InputLabel
                htmlFor="phoneNumber"
                className={errors.phoneNumber && touched.phoneNumber ? "label-err" : "label"}
              >
                Phone Number <span style={{ color: 'red' }}>*</span>
              </InputLabel>

              <PatternFormat
                displayType="input"
                format="+## ###### ####"
                customInput={TextField}
                name="phoneNumber"
                type="text"
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                placeholder="+44 000000 0000"
                error={!!(errors.phoneNumber && touched.phoneNumber)}
                value={values.phoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={hidePasswordCriteria}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {errors.phoneNumber && touched.phoneNumber ? (
                        <InfoIcon sx={{ color: "#DC3545" }} fontSize="medium" />
                      ) : (
                        <LocalPhoneIcon fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {errors.phoneNumber && touched.phoneNumber && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.phoneNumber}
                </Typography>
              )}
            </Grid>
            <Grid item md={12} xs={12}>
              <InputLabel
                htmlFor="email"
                className={errors.email && touched.email ? "label-err" : "label"}
              >
                Email <span style={{ color: 'red' }}>*</span>
              </InputLabel>

              <TextField
                name="email"
                type="email"
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                error={!!(errors.email && touched.email)}
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={hidePasswordCriteria}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {errors.email && touched.email ? (
                        <InfoIcon sx={{ color: "#DC3545" }} fontSize="medium" />
                      ) : (
                        <EmailIcon fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {errors.email && touched.email && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.email}
                </Typography>
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel
                htmlFor="password"
                className={errors.password && touched.password ? "label-err" : "label"}
              >
                Password <span style={{ color: 'red' }}>*</span>
              </InputLabel>

              <TextField
                name="password"
                type={state.password ? "text" : "password"}
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                error={!!(errors.password && touched.password)}
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => toggleState("showCriteria")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {state.password ? (
                        <Visibility
                          sx={{ cursor: "pointer" }}
                          fontSize="medium"
                          onClick={() => toggleState("Password")}
                        />
                      ) : (
                        <VisibilityOff
                          sx={{ cursor: "pointer" }}
                          fontSize="medium"
                          onClick={() => toggleState("Password")}
                        />
                      )}
                      {errors.password && touched.password && (
                        <InfoIcon sx={{ color: "#DC3545" }} fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {errors.password && touched.password && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.password === "Required" && "Required Field"}
                </Typography>
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <InputLabel
                htmlFor="confirmPassword"
                className={
                  errors.confirmPassword && touched.confirmPassword ? "label-err" : "label"
                }
              >
                Confirm Password <span style={{ color: 'red' }}>*</span>
              </InputLabel>
              <TextField
                name="confirmPassword"
                type={state.confirmPassword ? "text" : "password"}
                sx={{ background: "#fff" }}
                fullWidth
                size="small"
                variant="outlined"
                error={!!(errors.confirmPassword && touched.confirmPassword)}
                value={values.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={hidePasswordCriteria}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {state.password ? (
                        <Visibility
                          sx={{ cursor: "pointer" }}
                          fontSize="medium"
                          onClick={() => toggleState("confirmPassword")}
                        />
                      ) : (
                        <VisibilityOff
                          sx={{ cursor: "pointer" }}
                          fontSize="medium"
                          onClick={() => toggleState("confirmPassword")}
                        />
                      )}
                      {errors.password && touched.password && (
                        <InfoIcon sx={{ color: "#DC3545" }} fontSize="medium" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {errors.confirmPassword && touched.confirmPassword && (
                <Typography sx={{ color: "#DC3545" }} variant="body2">
                  {errors.confirmPassword}
                </Typography>
              )}
            </Grid>
          </Grid>
          <FormControlLabel
            className={errors.checked && touched.checked ? "error" : ""}
            sx={{ mt: "15px" }}
            label={
              <Typography variant="body1">
                I agree to
                <span className="secondary-color" onClick={() => handleOpenModal(3)}>
                  Terms & Conditions
                </span>{" "}
                and{" "}
                <span className="secondary-color" onClick={() => handleOpenModal(2)}>
                  Privacy Policy
                </span>
              </Typography>
            }
            control={
              <Checkbox
                className="error"
                name="checked"
                checked={values.checked}
                onChange={handleChange}
                sx={{
                  "&.Mui-checked": {
                    color: "#7FB341",
                  },
                }}
              />
            }
          />
          {errors.checked && touched.checked && (
            <Typography sx={{ color: "#DC3545" }} variant="body2">
              {errors.checked}
            </Typography>
          )}
          <Stack spacing={3} sx={{ textAlign: "center" }}>
            <Button
              className="btn-primary btn-text-uppercase"
              sx={{
                width: { xs: "100%", md: "auto", py: "10px !important" },
                marginTop: "15px",
              }}
              type="submit"
              disableRipple
              variant="contained"
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ color: "#FFFFFF", mr: 1 }} />
                  <span> Loading...</span>
                </>
              ) : (
                <span>SAVE AND CONtINUE</span>
              )}
            </Button>
            <Typography variant="body1">
              Already have an account? {""}
              <Link to="/authentication/signin" className="link">
                Sign In
              </Link>
            </Typography>
          </Stack>
        </form>
      }
      
      <CustomizedDialogs close={handleCloseModal} open={openModal} id={id} />
    </Box>
  );
};
export default CorporateAccount;
